import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import type { ProNotification } from '@erp/data-access/interfaces';
import {
  proNotificationDayBucket,
  type ProNotificationDayBucket,
} from '@erp/domain/pro';
import { NotificationItem } from './NotificationItem';

export type NotificationFilter = 'all' | 'unread';

const DAY_LABELS: Record<ProNotificationDayBucket, [key: string, fallback: string]> = {
  today: ['pro.notif.group.today', "Aujourd'hui"],
  yesterday: ['pro.notif.group.yesterday', 'Hier'],
  earlier: ['pro.notif.group.earlier', 'Plus ancien'],
};

const BUCKET_ORDER: ProNotificationDayBucket[] = ['today', 'yesterday', 'earlier'];

/**
 * Contenu du fil de notifications : en-tête, filtre, liste groupée par jour.
 *
 * Extrait du header pour que la page d'aperçu (`/notifications-apercu`) montre
 * exactement le composant expédié, et pas une maquette qui dériverait de lui.
 */
export function NotificationPanel({
  notifications,
  unreadCount,
  isLoading = false,
  now,
  onSelect,
  onMarkAllRead,
  isMarkingAllRead = false,
  maxHeight = 420,
}: {
  notifications: ProNotification[];
  unreadCount: number;
  isLoading?: boolean;
  now: Date;
  onSelect?: (notification: ProNotification) => void;
  onMarkAllRead?: () => void;
  isMarkingAllRead?: boolean;
  /** Nombre (px) ou expression CSS — la cloche passe `min(420px, 60vh)` pour
   *  que le panneau ne dépasse jamais du bas de l'écran sur mobile. */
  maxHeight?: number | string;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<NotificationFilter>('all');

  const visible = useMemo(
    () =>
      filter === 'unread'
        ? notifications.filter((n) => n.readAt === null)
        : notifications,
    [notifications, filter],
  );

  const groups = useMemo(() => {
    const map = new Map<ProNotificationDayBucket, ProNotification[]>();
    for (const n of visible) {
      const bucket = proNotificationDayBucket(n.createdAt, now);
      const list = map.get(bucket);
      if (list) list.push(n);
      else map.set(bucket, [n]);
    }
    return BUCKET_ORDER.filter((b) => map.has(b)).map(
      (b) => [b, map.get(b)!] as const,
    );
  }, [visible, now]);

  return (
    <div className="flex flex-col min-h-0">
      {/* En-tête */}
      <div
        className="px-4 pt-3 pb-2 shrink-0"
        style={{ borderBottom: '1px solid var(--pro-line)' }}
      >
        <div className="flex items-center justify-between gap-2">
          <span
            className="text-[15px] font-semibold flex items-center gap-2"
            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
          >
            {t('pro.header.notifications', 'Notifications')}
            {unreadCount > 0 && (
              <span
                className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                style={{
                  background: 'var(--pro-primary-subtle)',
                  color: 'var(--pro-primary-text)',
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </span>
          {unreadCount > 0 && onMarkAllRead && (
            <button
              type="button"
              onClick={onMarkAllRead}
              disabled={isMarkingAllRead}
              className="inline-flex items-center gap-1 text-[11px] font-semibold disabled:opacity-50 hover:opacity-80"
              style={{ color: 'var(--pro-primary-text)' }}
            >
              {isMarkingAllRead ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CheckCheck className="w-3.5 h-3.5" />
              )}
              {t('pro.header.mark_all_read_short', 'Tout lire')}
            </button>
          )}
        </div>

        {/* Filtre — masqué quand il n'y a rien à filtrer */}
        {notifications.length > 0 && (
          <div className="flex items-center gap-1 mt-2" role="tablist">
            {(['all', 'unread'] as const).map((key) => {
              const active = filter === key;
              const count =
                key === 'unread' ? unreadCount : notifications.length;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(key)}
                  className="text-[12px] font-medium px-2.5 py-1 rounded-lg"
                  style={{
                    background: active
                      ? 'var(--pro-primary-subtle)'
                      : 'transparent',
                    color: active
                      ? 'var(--pro-primary-text)'
                      : 'var(--pro-text-grey)',
                  }}
                >
                  {key === 'all'
                    ? t('pro.notif.filter_all', 'Tout')
                    : t('pro.notif.filter_unread', 'Non lues')}
                  {count > 0 && (
                    <span className="opacity-60"> · {count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Liste */}
      <div className="overflow-y-auto min-h-0" style={{ maxHeight }}>
        {isLoading && notifications.length === 0 ? (
          <div className="py-10 flex justify-center">
            <Loader2
              className="w-5 h-5 animate-spin"
              style={{ color: 'var(--pro-text-grey-light)' }}
            />
          </div>
        ) : visible.length === 0 ? (
          <EmptyState filtered={filter === 'unread'} />
        ) : (
          groups.map(([bucket, items]) => (
            <section key={bucket}>
              <h3
                className="sticky top-0 z-10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide backdrop-blur-sm"
                style={{
                  color: 'var(--pro-text-grey-light)',
                  background: 'rgba(255,255,255,0.92)',
                }}
              >
                {t(DAY_LABELS[bucket][0], DAY_LABELS[bucket][1])}
              </h3>
              {items.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  now={now}
                  {...(onSelect ? { onSelect } : {})}
                />
              ))}
            </section>
          ))
        )}
      </div>
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="py-10 px-6 text-center">
      <span
        className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center"
        style={{ background: 'var(--pro-grey-subtle)' }}
      >
        <Bell
          className="w-5 h-5"
          style={{ color: 'var(--pro-text-grey-light)' }}
        />
      </span>
      <p
        className="text-[13px] font-medium"
        style={{ color: 'var(--pro-text-dark)' }}
      >
        {filtered
          ? t('pro.notif.empty_unread_title', 'Tout est lu')
          : t('pro.header.no_notifications', 'Aucune nouvelle notification')}
      </p>
      <p className="text-[12px] mt-1" style={{ color: 'var(--pro-text-grey)' }}>
        {filtered
          ? t('pro.notif.empty_unread_body', 'Vous n’avez aucune notification en attente.')
          : t(
              'pro.notif.empty_body',
              'Vos devis, commandes et commissions apparaîtront ici.',
            )}
      </p>
    </div>
  );
}
